import type { Location, LocationDescriptorObject } from "history";
import isString from "lodash/isString";
import pick from "lodash/pick";
import qs from "query-string";
import {
	BooleanParam,
	type DecodedValueMap,
	type EncodedQuery,
	NumberParam,
	type QueryParamConfig,
	StringParam,
	decodeEnum,
	decodeQueryParams,
	encodeQueryParams,
	encodeString,
} from "serialize-query-params";

import { ApplicationDomain } from "~/models/dto";
import { toKeys } from "~/tools/to-keys";

import { analyticsParamsMap } from "../../../bottom_content/analytics/AnalyticsUtils";
import { viewFilterParam } from "../../../bottom_content/overview/overview-filter";
import { tracesExplorerSearchParams } from "../../../components/traces-explorer/traces-explorer-search-params";
import { QueryParam as MetricsQueryParam } from "../../../metrics/query-param";
import { NotificationsState } from "../../../notifications/NotificationsState";
import { dashboardFilterParam } from "../../../routes/dashboards/dashboard-filter";
import { dashboardVariablesParam } from "../../../routes/dashboards/dashboard-variables-param";
import { timelineParamsMap } from "../../../routes/timeline/TimelineQueryUtils";
import { RightPaneState } from "../right-pane/RightPaneState";
import { viewParamsMap } from "../view/viewpoint/ViewPointQueryUtils";

export const DomainParam: QueryParamConfig<undefined | ApplicationDomain> = {
	encode: encodeString,
	decode: (input): undefined | ApplicationDomain =>
		(decodeEnum(input, toKeys(ApplicationDomain)) as ApplicationDomain) ?? undefined,
};

const ALL_SEARCH_PARAMS = {
	...timelineParamsMap,
	...viewParamsMap,
	...analyticsParamsMap,
	...RightPaneState.searchParams,
	...NotificationsState.searchParams,
	...tracesExplorerSearchParams,
	metrics: MetricsQueryParam,
	metricTab: StringParam, // name of an active sub tab with metrics (check MetricBinding layout)
	viewFilter: viewFilterParam,
	dashboardFilter: dashboardFilterParam,
	dashboardVariables: dashboardVariablesParam,
	type: StringParam,
	domain: DomainParam,
	componentId: NumberParam as QueryParamConfig<number | undefined>,
	loggedOut: BooleanParam,
};

export type AllSearchParams = Partial<DecodedValueMap<typeof ALL_SEARCH_PARAMS>>;

export const STICKY_SEARCH_PARAMS: ReadonlyArray<keyof AllSearchParams> = ["domain"];

export interface DecodeAllSearchParamsOptions {
	/**
	 * If true, only params present in the query string will be decoded.
	 * Otherwise, all params are decoded, and missing params have default decoding values.
	 *
	 * @default false
	 *
	 * @example
	 * decodeAllSearchParams({ search: "?query=anything" }, { decodeOnlyPresentParams: true })
	 * // => { query: "anything" }
	 *
	 * decodeAllSearchParams({ search: "?query=anything" })
	 * // => { query: "anything", rightPane: RightPaneState.defaults, ...etc }
	 */
	decodeOnlyPresentParams?: boolean;
}

export const decodeAllSearchParams = (
	locationOrSearch: string | Location | LocationDescriptorObject,
	{ decodeOnlyPresentParams = false }: DecodeAllSearchParamsOptions = {},
): AllSearchParams => {
	const parsedQuery = qs.parse(isString(locationOrSearch) ? locationOrSearch : (locationOrSearch.search ?? ""));
	const params = decodeOnlyPresentParams ? pick(ALL_SEARCH_PARAMS, toKeys(parsedQuery)) : ALL_SEARCH_PARAMS;

	return decodeQueryParams(params, parsedQuery);
};

export const encodeAllSearchParams = (params: undefined | AllSearchParams): EncodedQuery =>
	encodeQueryParams(ALL_SEARCH_PARAMS, params ?? {});
